import { isAxiosError, type RawAxiosRequestConfig } from "axios";
import { defaults, omit, pickBy } from "lodash-es";

import { flowsApi, versionsApi, workspacesApi } from "src/api/endpoints";
import { Flow, FlowVersion, NodeBE } from "src/api/flowTypes";
import {
  FlowCreate,
  FlowUpdate,
  FlowVersionCreate,
  FlowVersionDuplicate,
} from "src/api/types";
import {
  FlowDb,
  FlowVersionDb,
  FlowVersionV2,
  GetFlowsApiV1FlowsGetOrderByEnum,
  WorkspaceDataplane,
} from "src/clients/flow-api";
import { defaultSchema } from "src/schema/schemaMappingUtils";

export const flowDbToFlowT = (flow: FlowDb): Flow => {
  return defaults(flow, {
    meta: { description: "" },
    flow_folder_id: null,
    versions: [],
    charts: [],
    chart_order: [],
  });
};

export const flowVersionDbToFlowVersionT = (
  version: FlowVersionDb,
): FlowVersion => {
  const graph = version.graph
    ? defaults(version.graph, {
        nodes: [],
        edges: [],
        node_groups: [],
      })
    : undefined;
  const defaultsWithoutCorrectGraph = defaults(version, {
    meta: {},
    input_schema: defaultSchema,
    output_schema: defaultSchema,
    parameters: [],
  });
  return { ...defaultsWithoutCorrectGraph, graph };
};

// @AUTH-2118: Refactor
// Makes sure we convert revisions to FlowVersionT as well, the type
// for flow versions used across all components.
export const flowVersionV2ToFlowVersionT = (
  version: FlowVersionV2,
): FlowVersion => {
  const graph = {
    created_at: version.created_at,
    updated_at: version.updated_at,
    meta: version.meta,
    id: version.graph_id ?? "",
    nodes: Object.values(version.graph.nodes) as NodeBE[],
    node_groups: Object.values(version.graph.node_groups),
    edges: Object.values(version.graph.edges),
  };

  const defaultsWithoutCorrectGraph = defaults(version, {
    meta: {},
    input_schema: defaultSchema,
    output_schema: defaultSchema,
    parameters: [],
  });

  return {
    ...defaultsWithoutCorrectGraph,
    graph,
    locks: version.locks ? Object.values(version.locks) : [],
  };
};

export const loadWorkspaces = async (
  includeInactive: boolean = false,
): Promise<WorkspaceDataplane[]> => {
  const response = await workspacesApi.getWorkspacesApiV1WorkspacesGet(
    undefined,
    undefined,
    undefined,
    includeInactive,
  );
  return response.data;
};

export const loadNavigableWorkspaces = async (): Promise<
  WorkspaceDataplane[]
> => {
  const response =
    await workspacesApi.getNavigableWorkspacesApiV1WorkspacesNavigableGet();
  return response.data;
};

export const createWorkspace = async (
  name: string,
  slug: string,
  organizationId: string,
  awsRegion: string,
  awsAccountId?: string,
): Promise<WorkspaceDataplane> => {
  const response = await workspacesApi.createWorkspaceApiV1WorkspacesPost({
    name: name,
    slug: slug,
    organization_id: organizationId,
    aws_region: awsRegion,
    ...(awsAccountId ? { aws_account_id: awsAccountId } : {}),
  });
  return response.data;
};

export const editWorkspace = async ({
  id,
  ...workspace
}: Pick<
  WorkspaceDataplane,
  "id" | "name" | "flow_services_version" | "settings"
>): Promise<WorkspaceDataplane> => {
  const response =
    await workspacesApi.updateWorkspaceApiV1WorkspacesWorkspaceIdPatch(
      id,
      workspace,
    );
  return response.data;
};

export const loadWorkspace = async (
  workspaceId: string,
  includeSettings: boolean = false,
): Promise<WorkspaceDataplane> => {
  const response =
    await workspacesApi.getWorkspaceApiV1WorkspacesWorkspaceIdGet(
      workspaceId,
      includeSettings,
    );
  return response.data;
};

// mirrored in flow-api, need to be modified synchronously
export const NO_FOLDER_FILTER = "no_folder";

export type SourceScopedRequestConfig = RawAxiosRequestConfig & {
  sourceScoped?: boolean;
};

export const authTokenConfig = (
  authToken?: string,
): SourceScopedRequestConfig =>
  authToken
    ? { headers: { Authorization: `Bearer ${authToken}` }, sourceScoped: true }
    : {};

export const loadFlows = async ({
  workspaceId,
  folderId,
  orderBy,
  skip,
  limit,
  include_internal_flows,
  includedTypes = ["none"],
  include_schemas = false,
  authToken,
}: {
  workspaceId?: string;
  folderId?: string;
  orderBy?: GetFlowsApiV1FlowsGetOrderByEnum;
  skip?: number;
  limit?: number;
  include_internal_flows?: boolean;
  includedTypes: string[];
  include_schemas?: boolean;
  authToken?: string;
}): Promise<Flow[]> => {
  const folderFilter = folderId;
  const response = await flowsApi.getFlowsApiV1FlowsGet(
    skip,
    limit,
    workspaceId,
    folderFilter,
    orderBy,
    // Only pass if true so we don't advertise its existance
    include_internal_flows ? include_internal_flows : undefined,
    include_schemas ? include_schemas : undefined,
    // include_archived_flow_versions: keep backend default (exclude archived)
    undefined,
    {
      params: {
        "filter[type][one_of]": includedTypes.join(","),
      },
      ...authTokenConfig(authToken),
    },
  );
  return response.data.map(flowDbToFlowT);
};

export const loadFlow = async (
  flowId: string,
  includeArchivedFlowVersions?: boolean,
): Promise<Flow> => {
  const response = await flowsApi.getFlowApiV1FlowsIdGet(
    flowId,
    includeArchivedFlowVersions,
  );
  return flowDbToFlowT(response.data);
};

export const createFlow = async (newFlow: FlowCreate): Promise<Flow> => {
  const response = await flowsApi.createFlowApiV1FlowsPost({
    name: newFlow.name,
    meta: { description: newFlow.description },
    workspace_id: newFlow.wsId,
    slug: newFlow.slug,
    // openapi generator generates the wrong type for flow_folder_id (undefiend instead of null)
    flow_folder_id: newFlow.flow_folder_id as string | undefined,
    initial_version_name: newFlow.initial_version_name,
    initial_version_meta: {
      created_by_id: newFlow.created_by_id,
      release_note: newFlow.initial_version_description,
    },
    review_configuration: newFlow.review_configuration,
    test_run_configuration: newFlow.test_run_configuration,
  });
  return flowDbToFlowT(response.data);
};

export const updateFlow = async (flowUpdate: FlowUpdate): Promise<void> => {
  await flowsApi.updateFlowApiV1FlowsIdPatch(
    flowUpdate.flowId,
    pickBy(
      omit(flowUpdate, ["flowId", "old_folder_id"]),
      (v) => v !== undefined,
    ),
  );
};

export const deleteFlow = async (flowId: string): Promise<void> =>
  (await flowsApi.deleteFlowApiV1FlowsFlowIdDelete(flowId)).data;

export const loadVersion = async (
  versionId: string,
  options: {
    cached?: FlowVersion;
    authToken?: string;
  } = {},
): Promise<FlowVersion> => {
  try {
    const response = await versionsApi.getFlowVersionApiV1FlowVersionsIdGet(
      versionId,
      /* IfMatch */ undefined,
      /* IfNoneMatch */ options?.cached?.etag,
      authTokenConfig(options.authToken),
    );
    return flowVersionDbToFlowVersionT(response.data);
  } catch (err) {
    // Axios treats 304 as an error https://github.com/axios/axios/issues/703
    // 429 is a retryable error
    if (isAxiosError(err) && err.response?.status === 304) {
      return options.cached!;
    } else if (
      isAxiosError(err) &&
      err.response?.status === 429 &&
      options.cached
    ) {
      return options.cached;
    } else {
      throw err;
    }
  }
};

// Override browser behaviour of including
// the If-None-Match header automatically
// to get the latest version of the children
export const loadVersionOverridingIfNoneMatch = async (
  versionId: string,
): Promise<FlowVersion> => {
  const response = await versionsApi.getFlowVersionApiV1FlowVersionsIdGet(
    versionId,
    /* IfMatch */ undefined,
    /* IfNoneMatch */ "xxxxxxxx",
  );
  return flowVersionDbToFlowVersionT(response.data);
};

export const createFlowVersion = async (
  flowVersionCreate: FlowVersionCreate,
): Promise<FlowVersion> => {
  const response = await versionsApi.createFlowVersionApiV1FlowVersionsPost({
    name: flowVersionCreate.name,
    flow_id: flowVersionCreate.flowId,
    meta: {
      release_note: flowVersionCreate.release_note,
      created_by_id: flowVersionCreate.created_by_id,
    },
  });
  return flowVersionDbToFlowVersionT(response.data);
};

export const duplicateFlowVersion = async (
  flowVersionDuplicate: FlowVersionDuplicate,
): Promise<FlowVersion> => {
  const response = await versionsApi.duplicateApiV1FlowVersionsIdDuplicatePost(
    flowVersionDuplicate.flowVersionId,
    {
      name: flowVersionDuplicate.name,
      include_comments: flowVersionDuplicate.include_comments,
      flow_id: flowVersionDuplicate.targetFlowId,
      meta: {
        release_note: flowVersionDuplicate.release_note,
        created_by_id: flowVersionDuplicate.created_by_id,
      },
    },
  );
  return flowVersionDbToFlowVersionT(response.data);
};
